<template>
    <div
        class="modal fade"
        :class="{ show: isVisible }"
        :style="{ display: isVisible ? 'block' : 'none' }"
        tabindex="-1"
    >
        <div class="modal-dialog modal-lg">
            <div class="modal-content">
                <div class="modal-header bg-gradient-primary text-white">
                    <h5 class="modal-title">
                        <i :class="mode === 'create' ? 'fas fa-user-plus' : 'fas fa-user-edit'"></i>
                        {{ mode === 'create' ? 'Créer un utilisateur' : 'Modifier un utilisateur' }}
                    </h5>
                    <button type="button" class="btn-close btn-close-white" @click="close"></button>
                </div>

                <div class="modal-body">
                    <form @submit.prevent="submitForm">
                        <div class="row">
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label for="name" class="form-label">
                                        Nom <span class="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        class="form-control"
                                        id="name"
                                        v-model="formData.name"
                                        :class="{ 'is-invalid': errors.name }"
                                        placeholder="Nom de l'utilisateur"
                                    >
                                    <div class="invalid-feedback" v-if="errors.name">
                                        {{ errors.name }}
                                    </div>
                                </div>
                            </div>

                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label for="email" class="form-label">
                                        Email <span class="text-danger">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        class="form-control"
                                        id="email"
                                        v-model="formData.email"
                                        :class="{ 'is-invalid': errors.email }"
                                        placeholder="email@exemple.com"
                                    >
                                    <div class="invalid-feedback" v-if="errors.email">
                                        {{ errors.email }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="mb-3">
                            <label class="form-label">
                                Groupes
                            </label>

                            <div class="groups-checkboxes">
                                <!-- État de chargement -->
                                <div v-if="!groups || groups.length === 0" class="text-muted text-center py-3">
                                    <i class="fas fa-inbox mb-2"></i>
                                    <p class="mb-0">Aucun groupe disponible</p>
                                    <small>Créez d'abord des groupes dans la section "Groupes"</small>
                                </div>
                                <!-- Grille des groupes -->
                                <div v-else class="groups-grid">
                                    <!-- Option Sans groupe comme un groupe spécial -->
                                    <div class="group-checkbox-item no-group-item">
                                        <div class="form-check">
                                            <input
                                                type="checkbox"
                                                class="form-check-input"
                                                id="group-no-group"
                                                value="null"
                                                v-model="formData.groups"
                                            >
                                            <label class="form-check-label" for="group-no-group">
                                                <i class="fas fa-inbox text-warning"></i>
                                                <strong>Sans groupe</strong>
                                                <small class="d-block text-muted">Voir les devices sans groupe</small>
                                            </label>
                                        </div>
                                    </div>

                                    <!-- Groupes normaux -->
                                    <div
                                        v-for="group in groups"
                                        :key="group.id"
                                        class="group-checkbox-item"
                                    >
                                        <div class="form-check">
                                            <input
                                                type="checkbox"
                                                class="form-check-input"
                                                :id="'group-' + group.id"
                                                :value="group.id"
                                                v-model="formData.groups"
                                            >
                                            <label class="form-check-label" :for="'group-' + group.id">
                                                <i class="fas fa-layer-group text-primary"></i>
                                                <strong>{{ group.name }}</strong>
                                                <span v-if="group.description" class="text-muted ms-2">- {{ group.description }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <small class="form-text text-muted" v-if="groups && groups.length > 0">
                                <i class="fas fa-info-circle"></i>
                                {{ formData.groups.length }} groupe(s) sélectionné(s)
                            </small>
                        </div>

                        <div class="mb-3">
                            <label for="timezone" class="form-label">
                                Fuseau horaire
                            </label>
                            <select
                                class="form-select"
                                id="timezone"
                                v-model="formData.timezone"
                            >
                                <option value="-12">GMT-12:00</option>
                                <option value="-11">GMT-11:00</option>
                                <option value="-10">GMT-10:00</option>
                                <option value="-9.5">GMT-09:30</option>
                                <option value="-9">GMT-09:00</option>
                                <option value="-8">GMT-08:00</option>
                                <option value="-7">GMT-07:00</option>
                                <option value="-6">GMT-06:00</option>
                                <option value="-5">GMT-05:00</option>
                                <option value="-4.5">GMT-04:30</option>
                                <option value="-4">GMT-04:00</option>
                                <option value="-3.5">GMT-03:30</option>
                                <option value="-3">GMT-03:00</option>
                                <option value="-2">GMT-02:00</option>
                                <option value="-1">GMT-01:00</option>
                                <option value="0">GMT±00:00 (Greenwich Mean Time)</option>
                                <option value="1">GMT+01:00</option>
                                <option value="2">GMT+02:00</option>
                                <option value="3">GMT+03:00</option>
                                <option value="3.5">GMT+03:30</option>
                                <option value="4">GMT+04:00</option>
                                <option value="4.5">GMT+04:30</option>
                                <option value="5">GMT+05:00</option>
                                <option value="5.5">GMT+05:30</option>
                                <option value="5.75">GMT+05:45</option>
                                <option value="6">GMT+06:00</option>
                                <option value="6.5">GMT+06:30</option>
                                <option value="7">GMT+07:00</option>
                                <option value="8">GMT+08:00</option>
                                <option value="8.75">GMT+08:45</option>
                                <option value="9">GMT+09:00</option>
                                <option value="9.5">GMT+09:30</option>
                                <option value="10">GMT+10:00</option>
                                <option value="10.5">GMT+10:30</option>
                                <option value="11">GMT+11:00</option>
                                <option value="12">GMT+12:00</option>
                                <option value="12.75">GMT+12:45</option>
                                <option value="13">GMT+13:00</option>
                                <option value="14">GMT+14:00</option>
                            </select>
                        </div>

                        <div class="mb-3">
                            <div class="form-check form-switch">
                                <input
                                    type="checkbox"
                                    class="form-check-input"
                                    id="isActive"
                                    v-model="formData.is_active"
                                >
                                <label class="form-check-label" for="isActive">
                                    Compte actif
                                </label>
                            </div>
                        </div>

                        <div class="mb-3">
                            <div class="form-check form-switch">
                                <input
                                    type="checkbox"
                                    class="form-check-input"
                                    id="isAdmin"
                                    v-model="formData.is_admin"
                                >
                                <label class="form-check-label" for="isAdmin">
                                    <i class="fas fa-shield-alt text-primary"></i>
 Administrateur
                                </label>
                                <small class="form-text text-muted d-block">
                                    Les administrateurs ont accès au panneau d'administration
                                </small>
                            </div>
                        </div>

                        <div class="row" v-if="mode === 'create'">
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label for="password" class="form-label">
                                        Mot de passe <span class="text-danger">*</span>
                                    </label>
                                    <input
                                        type="password"
                                        class="form-control"
                                        id="password"
                                        v-model="formData.password"
                                        :class="{ 'is-invalid': errors.password }"
                                        placeholder="Mot de passe"
                                    >
                                    <div class="invalid-feedback" v-if="errors.password">
                                        {{ errors.password }}
                                    </div>
                                </div>
                            </div>

                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label for="password_confirmation" class="form-label">
                                        Confirmer le mot de passe <span class="text-danger">*</span>
                                    </label>
                                    <input
                                        type="password"
                                        class="form-control"
                                        id="password_confirmation"
                                        v-model="formData.password_confirmation"
                                        :class="{ 'is-invalid': errors.password_confirmation }"
                                        placeholder="Confirmer le mot de passe"
                                    >
                                    <div class="invalid-feedback" v-if="errors.password_confirmation">
                                        {{ errors.password_confirmation }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="row" v-if="mode === 'edit'">
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label for="newPassword" class="form-label">
                                        Nouveau mot de passe (optionnel)
                                    </label>
                                    <input
                                        type="password"
                                        class="form-control"
                                        id="newPassword"
                                        v-model="formData.password"
                                        :class="{ 'is-invalid': errors.password }"
                                        placeholder="Laisser vide pour ne pas changer"
                                    >
                                    <div class="invalid-feedback" v-if="errors.password">
                                        {{ errors.password }}
                                    </div>
                                    <small class="form-text text-muted">
                                        Remplissez uniquement si vous souhaitez changer le mot de passe
                                    </small>
                                </div>
                            </div>

                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label for="newPassword_confirmation" class="form-label">
                                        Confirmer le nouveau mot de passe
                                    </label>
                                    <input
                                        type="password"
                                        class="form-control"
                                        id="newPassword_confirmation"
                                        v-model="formData.password_confirmation"
                                        :class="{ 'is-invalid': errors.password_confirmation }"
                                        :disabled="!formData.password"
                                        placeholder="Confirmer le mot de passe"
                                    >
                                    <div class="invalid-feedback" v-if="errors.password_confirmation">
                                        {{ errors.password_confirmation }}
                                    </div>
                                    <small class="form-text text-muted" v-if="!formData.password">
                                        Ce champ est activé uniquement si vous saisissez un nouveau mot de passe
                                    </small>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>

                <div class="modal-footer bg-light">
                    <button type="button" class="btn btn-secondary" @click="close">
                        <i class="fas fa-times"></i> Annuler
                    </button>
                    <button
                        type="button"
                        class="btn btn-primary"
                        @click="submitForm"
                        :disabled="isSubmitting"
                    >
                        <i class="fas" :class="isSubmitting ? 'fa-spinner fa-spin' : 'fa-save'"></i>
                        {{ isSubmitting ? 'Enregistrement...' : (mode === 'create' ? 'Créer' : 'Enregistrer') }}
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- Backdrop -->
    <div
        v-if="isVisible"
        class="modal-backdrop fade show"
        @click="close"
    ></div>
</template>

<script>
import { mapActions } from 'vuex';
import { useToast } from 'vue-toastification';

export default {
    name: 'UsersForm',
    props: {
        user: {
            type: Object,
            default: null
        },
        groups: {
            type: Array,
            default: () => []
        },
        mode: {
            type: String,
            default: 'create',
            validator: (value) => ['create', 'edit'].includes(value)
        }
    },
    data() {
        return {
            isVisible: true,
            isSubmitting: false,
            formData: {
                id: null,
                name: '',
                email: '',
                groups: [],
                timezone: '0',
                is_active: true,
                is_admin: false,
                password: '',
                password_confirmation: ''
            },
            errors: {}
        };
    },
    mounted() {
        if (this.mode === 'edit' && this.user) {
            this.initializeFormData();
        }
    },
    methods: {
        ...mapActions({
            createUser: 'users/create/create',
            updateUser: 'users/update/update'
        }),

        initializeFormData() {
            this.formData = {
                id: this.user.id,
                name: this.user.name || '',
                email: this.user.email || '',
                groups: this.user.groups ? [...this.user.groups] : [],
                timezone: this.user.timezone !== undefined && this.user.timezone !== null
                    ? this.user.timezone
                    : '0',
                is_active: this.user.is_active == 1,
                is_admin: this.user.is_admin == 1 || this.user.is_admin === true,
                password: '',
                password_confirmation: ''
            };
        },

        validateForm() {
            this.errors = {};

            // Validation nom
            if (!this.formData.name || this.formData.name.trim() === '') {
                this.errors.name = 'Le nom est requis';
            }

            // Validation email
            if (!this.formData.email || this.formData.email.trim() === '') {
                this.errors.email = 'L\'email est requis';
            } else if (!this.isValidEmail(this.formData.email)) {
                this.errors.email = 'L\'email n\'est pas valide';
            }

            // Validation mot de passe (création)
            if (this.mode === 'create') {
                if (!this.formData.password) {
                    this.errors.password = 'Le mot de passe est requis';
                } else if (this.formData.password.length < 6) {
                    this.errors.password = 'Le mot de passe doit contenir au moins 6 caractères';
                }

                if (this.formData.password !== this.formData.password_confirmation) {
                    this.errors.password_confirmation = 'Les mots de passe ne correspondent pas';
                }
            }

            // Validation mot de passe (édition si fourni)
            if (this.mode === 'edit' && this.formData.password) {
                if (this.formData.password.length < 6) {
                    this.errors.password = 'Le mot de passe doit contenir au moins 6 caractères';
                }

                if (this.formData.password !== this.formData.password_confirmation) {
                    this.errors.password_confirmation = 'Les mots de passe ne correspondent pas';
                }
            }

            return Object.keys(this.errors).length === 0;
        },

        isValidEmail(email) {
            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return re.test(email);
        },

        async submitForm() {
            if (!this.validateForm()) {
                return;
            }

            this.isSubmitting = true;
            const toast = useToast();

            try {
                // Préparer les données
                const data = {
                    name: this.formData.name.trim(),
                    email: this.formData.email.trim(),
                    groups: this.formData.groups, // Peut contenir "null" pour les devices sans groupe
                    timezone: this.formData.timezone,
                    is_active: this.formData.is_active ? 1 : 0,
                    is_admin: this.formData.is_admin ? 1 : 0
                };

                // Ajouter le mot de passe si fourni
                if (this.formData.password) {
                    data.password = this.formData.password;
                    if (this.mode === 'create') {
                        data.password_confirmation = this.formData.password_confirmation;
                    }
                }

                if (this.mode === 'create') {
                    // Création
                    await this.createUser(data);
                    toast.success('Utilisateur créé avec succès');
                } else {
                    // Mise à jour
                    data.id = this.formData.id;
                    await this.updateUser(data);
                    // Le toast est déjà affiché par l'action Vuex (update/actions.js)
                }

                // Émettre l'événement de succès
                this.$emit('saved');

            } catch (error) {
                console.error('Erreur lors de l\'enregistrement:', error);
                const errorMessage = error.response?.data?.message || error.message || 'Erreur lors de l\'enregistrement';
                toast.error(errorMessage);

                // Gérer les erreurs de validation Laravel
                if (error.response?.data?.errors) {
                    this.errors = error.response.data.errors;
                }
            } finally {
                this.isSubmitting = false;
            }
        },

        close() {
            this.isVisible = false;
            this.$emit('close');
        }
    }
};
</script>

<style scoped>
.modal {
    z-index: 1050;
}

.modal-backdrop {
    z-index: 1040;
}

.bg-gradient-primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.form-control:focus,
.form-select:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

.form-check-input:checked {
    background-color: #667eea;
    border-color: #667eea;
}

.form-control[multiple] {
    padding: 0.5rem;
}

.form-control[multiple] option {
    padding: 0.5rem;
}

.groups-checkboxes {
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid #dee2e6;
    border-radius: 0.5rem;
    padding: 1rem;
    background: #f8f9fa;
}

.groups-checkboxes::-webkit-scrollbar {
    width: 8px;
}

.groups-checkboxes::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
}

.groups-checkboxes::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 4px;
}

.groups-checkboxes::-webkit-scrollbar-thumb:hover {
    background: #a0aec0;
}

.groups-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 0.75rem;
}

.group-checkbox-item {
    padding: 0.75rem;
    border-radius: 0.5rem;
    transition: all 0.2s ease;
    background: white;
    border: 1px solid #e9ecef;
}

.group-checkbox-item:hover {
    background: #eef2ff;
    border-color: #c7d2fe;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
}

.group-checkbox-item .form-check {
    margin: 0;
}

.group-checkbox-item .form-check-input {
    cursor: pointer;
    width: 1.25rem;
    height: 1.25rem;
    margin-top: 0.1rem;
}

.group-checkbox-item .form-check-input:checked {
    background-color: #667eea;
    border-color: #667eea;
}

.group-checkbox-item .form-check-label {
    cursor: pointer;
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    margin: 0;
    font-size: 0.95rem;
    user-select: none;
    line-height: 1.4;
}

.group-checkbox-item .form-check-label i {
    margin-top: 0.15rem;
}

/* Groupe spécial "Sans groupe" */
.group-checkbox-item.no-group-item {
    background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
    border: 2px dashed #f59e0b;
}

.group-checkbox-item.no-group-item:hover {
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
    border-color: #d97706;
}

.group-checkbox-item.no-group-item .form-check-input:checked {
    background-color: #f59e0b;
    border-color: #f59e0b;
}

.group-checkbox-item.no-group-item .form-check-label small {
    font-size: 0.75rem;
    color: #b45309;
}

.invalid-feedback {
    display: block;
}
</style>
